import { format as formatDate } from "date-fns";
import { ActionTypes } from "./actions.js";
import { getInitialState, initialSearchSegment, initialFareCalendarState, SEARCH_DATE_FORMAT } from "./state.js";
import { ANYWHERE } from "../utils/constants.js";
import { reportError } from "@bookingcom/flights-core";
const initialState = getInitialState();
const reducer = (state = initialState, action) => {
    switch (action.type) {
        case ActionTypes.setType: {
            const { type } = action.payload;
            let newSearchSegments = [...state.searchSegments];
            try {
                newSearchSegments = JSON.parse(JSON.stringify(state.searchSegments));
                if (type === "MULTISTOP") {
                    newSearchSegments.forEach((segment) => {
                        segment.from.splice(1);
                        segment.to.splice(1);
                        const indexForRemoval = segment.to.findIndex((item) => item.type === ANYWHERE || item.type === "COUNTRY");
                        if (indexForRemoval > -1) {
                            segment.to.splice(indexForRemoval);
                        }
                        segment.returnDate = undefined;
                    });
                }
                else {
                    newSearchSegments.splice(1);
                    return { ...state, type, searchSegments: newSearchSegments };
                }
            }
            catch (error) {
                if (typeof reportError === "function") {
                    reportError(error, "Error in @flights/searchbox reducer ActionTypes.setType while deep cloning search segments");
                }
            }
            return { ...state, type, searchSegments: newSearchSegments };
        }
        case ActionTypes.removeSegment: {
            const { searchSegmentIndex } = action.payload;
            const newSearchSegments = [...state.searchSegments];
            newSearchSegments.splice(searchSegmentIndex, 1);
            return { ...state, searchSegments: newSearchSegments };
        }
        case ActionTypes.addSegment: {
            const newSearchSegments = [...state.searchSegments, initialSearchSegment];
            return { ...state, searchSegments: newSearchSegments };
        }
        case ActionTypes.clearSegments: {
            const firstSegment = state.searchSegments[0] || initialSearchSegment;
            const newSearchSegments = [firstSegment];
            return { ...state, searchSegments: newSearchSegments };
        }
        case ActionTypes.setFrom: {
            const { from, searchSegmentIndex = 0 } = action.payload;
            const newSearchSegments = [...state.searchSegments];
            const currentSearchSegment = { ...newSearchSegments[searchSegmentIndex] };
            currentSearchSegment.from = from;
            newSearchSegments[searchSegmentIndex] = currentSearchSegment;
            if (currentSearchSegment.from.length > 1 &&
                currentSearchSegment.to.some((destination) => destination.type.toLowerCase() === ANYWHERE.toLowerCase() || destination.type.toLowerCase() === "country")) {
                currentSearchSegment.to = [];
            }
            return { ...state, searchSegments: newSearchSegments };
        }
        case ActionTypes.setTo: {
            const { to, searchSegmentIndex = 0 } = action.payload;
            const newSearchSegments = [...state.searchSegments];
            const currentSearchSegment = { ...newSearchSegments[searchSegmentIndex] };
            const countryLocations = to.filter((location) => location.type === "COUNTRY");
            const nonCountryLocations = to.filter((location) => location.type !== "COUNTRY");
            const hadCountry = currentSearchSegment.to.some((location) => location.type === "COUNTRY");
            const isAddingNonCountryToCountry = hadCountry && nonCountryLocations.length > 0;
            if (isAddingNonCountryToCountry) {
                currentSearchSegment.to = nonCountryLocations;
            }
            else if (countryLocations.length > 0) {
                currentSearchSegment.to = [countryLocations[countryLocations.length - 1]];
            }
            else if (nonCountryLocations.length > 0) {
                currentSearchSegment.to = nonCountryLocations;
            }
            else {
                currentSearchSegment.to = to;
            }
            newSearchSegments[searchSegmentIndex] = currentSearchSegment;
            if (currentSearchSegment.from.length > 1 &&
                currentSearchSegment.to.some((destination) => destination.type.toLowerCase() === ANYWHERE.toLowerCase() || destination.type.toLowerCase() === "country")) {
                currentSearchSegment.to = [];
            }
            return { ...state, searchSegments: newSearchSegments };
        }
        case ActionTypes.setDateRange: {
            let nfrom, nto;
            const { range, searchSegmentIndex = 0 } = action.payload;
            const { from, to, offerAccuracyTrackerId } = range;
            if (typeof from === "object") {
                nfrom = formatDate(from, SEARCH_DATE_FORMAT);
            }
            if (typeof to === "object") {
                nto = formatDate(to, SEARCH_DATE_FORMAT);
            }
            const newSearchSegments = [...state.searchSegments];
            const currentSearchSegment = { ...newSearchSegments[searchSegmentIndex] };
            currentSearchSegment.departureDate = nfrom;
            currentSearchSegment.returnDate = nto;
            currentSearchSegment.offerAccuracyTrackerId = offerAccuracyTrackerId;
            if (state.type === "MULTISTOP") {
                currentSearchSegment.returnDate = undefined;
            }
            newSearchSegments[searchSegmentIndex] = currentSearchSegment;
            return { ...state, searchSegments: newSearchSegments };
        }
        case ActionTypes.setOccupancyAndCabinClass:
            return { ...state, ...action.payload };
        case ActionTypes.setOccupancy:
            return { ...state, ...action.payload };
        case ActionTypes.setCabinClass:
            return { ...state, ...action.payload };
        case ActionTypes.setIsDirect:
            return { ...state, isDirect: action.payload.isDirect };
        case ActionTypes.swapFromTo: {
            const { searchSegmentIndex = 0 } = action.payload;
            const newSearchSegments = [...state.searchSegments];
            const currentSearchSegment = { ...newSearchSegments[searchSegmentIndex] };
            const { from, to } = currentSearchSegment;
            if (to.some((item) => item.type === ANYWHERE || item.type === "COUNTRY")) {
                currentSearchSegment["from"] = [];
            }
            else {
                currentSearchSegment["from"] = to;
            }
            currentSearchSegment["to"] = from;
            newSearchSegments[searchSegmentIndex] = currentSearchSegment;
            return { ...state, searchSegments: newSearchSegments };
        }
        case ActionTypes.resetSegment: {
            const { searchSegmentIndex = 0 } = action.payload;
            const newSearchSegments = [...state.searchSegments];
            newSearchSegments[searchSegmentIndex] = initialSearchSegment;
            return { ...state, searchSegments: newSearchSegments };
        }
        case ActionTypes.setInitial:
            return {
                ...action.payload.initialValues,
                fareCalendar: initialFareCalendarState,
                dataCollectionPrices: {},
                recentSearchHistory: state.recentSearchHistory
            };
        case ActionTypes.fareCalendarStart:
            return {
                ...state,
                fareCalendar: {
                    ...state.fareCalendar,
                    loading: true,
                    error: null
                }
            };
        case ActionTypes.fareCalendarSuccess: {
            const sourceRange = `${action.payload.dateRange[0]}_${action.payload.dateRange[1]}${action.payload.departureDate ? `_${action.payload.departureDate}` : ""}`;
            const sourceRangeData = {};
            if (action.payload.data) {
                action.payload.data.forEach((flight) => {
                    const key = action.payload.departureDate
                        ? flight.returnDepartureDate || flight.outboundDepartureDate
                        : flight.outboundDepartureDate;
                    sourceRangeData[key] = flight;
                });
            }
            return {
                ...state,
                fareCalendar: {
                    data: {
                        ...state.fareCalendar.data,
                        [sourceRange]: sourceRangeData
                    },
                    fetchedRanges: [
                        ...state.fareCalendar.fetchedRanges,
                        {
                            start: action.payload.dateRange[0],
                            end: action.payload.dateRange[1],
                            ...(action.payload.departureDate && { departureDate: action.payload.departureDate })
                        }
                    ],
                    loading: false,
                    error: null,
                    maxOfferWindowDays: action.payload.maxOfferWindowDays ?? state.fareCalendar.maxOfferWindowDays
                }
            };
        }
        case ActionTypes.fareCalendarError:
            return {
                ...state,
                fareCalendar: {
                    ...state.fareCalendar,
                    loading: false,
                    error: action.payload.error
                }
            };
        case ActionTypes.fareCalendarClear:
            return {
                ...state,
                fareCalendar: initialFareCalendarState
            };
        case ActionTypes.fareCalendarDataCollectionSuccess:
            return {
                ...state,
                dataCollectionPrices: action.payload.prices
            };
        case ActionTypes.setRecentSearchHistory:
            return {
                ...state,
                recentSearchHistory: action.payload.recentSearchHistory
            };
        case ActionTypes.selectRecentSearch:
            return state;
        default:
            return state;
    }
};
export { reducer, getInitialState };
